export type ErrorToastHandler = (message: string) => void;

type QueuedToast = {
  message: string;
};

/**
 * Creates a bridge between early error producers and the real toast UI.
 *
 * Startup order in this app is split into two phases:
 * 1. Query/API modules can emit errors as soon as they are imported.
 * 2. The actual toast UI exists only after App.vue creates the toast provider.
 *
 * Any error raised during that gap would normally be lost. This bridge buffers
 * toast requests until App.vue registers the final UI handler.
 */
export const createErrorToastBridge = () => {
  let activeHandler: ErrorToastHandler | null = null;
  const queuedToasts: Array<QueuedToast> = [];

  const flushQueuedToasts = (): void => {
    if (!activeHandler || queuedToasts.length === 0) {
      return;
    }

    const toastsToFlush = queuedToasts.splice(0);

    for (const toast of toastsToFlush) {
      activeHandler(toast.message);
    }
  };

  const dispatchOrQueueToast = (toast: QueuedToast): void => {
    if (activeHandler) {
      activeHandler(toast.message);
      return;
    }

    queuedToasts.push(toast);
  };

  /**
   * Registers the real UI toast handler.
   *
   * This is called from App.vue once the toast provider is ready. Any errors
   * buffered during bootstrap are replayed in the same order they were received.
   */
  const setErrorToastHandler = (handler: ErrorToastHandler): void => {
    activeHandler = handler;
    flushQueuedToasts();
  };

  /**
   * Public entrypoint used by Vue Query and legacy API code.
   *
   * Before the app is fully mounted the toast is buffered; afterwards it is
   * delivered immediately to the active UI handler.
   */
  const showErrorToast = (message: string): void => {
    dispatchOrQueueToast({ message });
  };

  return {
    setErrorToastHandler,
    showErrorToast,
  };
};
